import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Input } from '../../../ui';
import {
  recoveryPrograms,
  programCategories,
  type ProgramCategory,
  type RecoveryProgram,
} from '../../../../content/meeting-programs';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useOnboarding, type CustomProgram } from '../../../../stores/onboardingStore';

interface ProgramsStepProps {
  selectedPrograms: string[];
  onSubmit: (programs: string[]) => void;
}

const categoryOrder: ProgramCategory[] = ['substance', 'behavioral', 'family'];

function ProgramCard({
  program,
  isSelected,
  onToggle,
  index,
}: {
  program: RecoveryProgram;
  isSelected: boolean;
  onToggle: () => void;
  index: number;
}) {
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.3 }}
      className={`
        relative group text-left p-4 rounded-xl border-2 transition-all duration-200
        ${
          isSelected
            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-md'
            : 'border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 hover:border-surface-300 dark:hover:border-surface-600'
        }
      `}
    >
      {/* Selection indicator */}
      <div
        className={`
        absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
        ${
          isSelected
            ? 'border-teal-500 bg-teal-500 shadow-sm'
            : 'border-surface-300 dark:border-surface-600 group-hover:border-surface-400'
        }
      `}
      >
        {isSelected && <CheckIcon className="w-4 h-4 text-white" />}
      </div>

      {/* Program badge */}
      <div
        className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white font-bold text-sm mb-2"
        style={{ backgroundColor: program.color }}
      >
        {program.shortName.slice(0, 3)}
      </div>

      {/* Program info */}
      <h4 className="font-semibold text-surface-800 dark:text-white text-sm pr-6">
        {program.name}
      </h4>
      <p className="text-xs text-surface-500 dark:text-surface-400 mt-1 leading-relaxed">
        {program.description}
      </p>
    </motion.button>
  );
}

function CategorySection({
  category,
  programs,
  selectedPrograms,
  onToggle,
  isExpanded,
  onToggleExpand,
}: {
  category: ProgramCategory;
  programs: RecoveryProgram[];
  selectedPrograms: string[];
  onToggle: (id: string) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const categoryInfo = programCategories[category];
  const selectedInCategory = programs.filter((p) => selectedPrograms.includes(p.id)).length;

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 rounded-lg bg-surface-100 dark:bg-surface-800 hover:bg-surface-150 dark:hover:bg-surface-750 transition-colors"
      >
        <div className="text-left">
          <h3 className="font-semibold text-surface-800 dark:text-white flex items-center gap-2">
            {categoryInfo.label}
            {selectedInCategory > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 dark:bg-accent-900/50 dark:text-accent-300">
                {selectedInCategory} selected
              </span>
            )}
          </h3>
          <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
            {categoryInfo.description}
          </p>
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-surface-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
              {programs.map((program, index) => (
                <ProgramCard
                  key={program.id}
                  program={program}
                  isSelected={selectedPrograms.includes(program.id)}
                  onToggle={() => onToggle(program.id)}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ProgramsStep({ selectedPrograms, onSubmit }: ProgramsStepProps) {
  const [selected, setSelected] = useState<string[]>(selectedPrograms);
  const [expandedCategories, setExpandedCategories] = useState<Set<ProgramCategory>>(
    new Set() // Start with all accordions closed
  );
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramAbbreviation, setNewProgramAbbreviation] = useState('');
  const [newProgramDescription, setNewProgramDescription] = useState('');
  const [newProgramWebsite, setNewProgramWebsite] = useState('');
  const [abbreviationError, setAbbreviationError] = useState('');
  const { data, addCustomProgram, removeCustomProgram } = useOnboarding();

  const toggleProgram = (programId: string) => {
    setSelected((prev) =>
      prev.includes(programId) ? prev.filter((id) => id !== programId) : [...prev, programId]
    );
  };

  const toggleCategory = (category: ProgramCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleAddCustomProgram = () => {
    if (!newProgramName.trim() || !newProgramAbbreviation.trim()) return;

    // Check for duplicate abbreviations (case-insensitive)
    const upperAbbr = newProgramAbbreviation.trim().toUpperCase();
    const isDuplicate = data.customPrograms?.some(
      (p) => p.abbreviation.toUpperCase() === upperAbbr
    );

    if (isDuplicate) {
      setAbbreviationError('A program with this abbreviation already exists');
      return;
    }

    // Validate abbreviation length
    if (upperAbbr.length < 2 || upperAbbr.length > 10) {
      setAbbreviationError('Abbreviation must be 2-10 characters');
      return;
    }

    const customProgram: CustomProgram = {
      id: `custom-${Date.now()}`,
      name: newProgramName.trim(),
      abbreviation: upperAbbr,
      description: newProgramDescription.trim() || undefined,
      website: newProgramWebsite.trim() || undefined,
    };

    addCustomProgram(customProgram);
    setNewProgramName('');
    setNewProgramAbbreviation('');
    setNewProgramDescription('');
    setNewProgramWebsite('');
    setAbbreviationError('');
  };

  const handleRemoveCustomProgram = (id: string) => {
    removeCustomProgram(id);
    setSelected((prev) => prev.filter((pid) => pid !== id));
  };

  const handleSubmit = () => {
    onSubmit(selected);
  };

  const programsByCategory = categoryOrder.reduce(
    (acc, category) => {
      acc[category] = recoveryPrograms.filter((p) => p.category === category);
      return acc;
    },
    {} as Record<ProgramCategory, RecoveryProgram[]>
  );

  return (
    <div>
      {/* Intro text */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <p className="text-surface-600 dark:text-surface-400">
          Select the recovery programs you're participating in. You can always change this later.
        </p>
        {selected.length > 0 && (
          <p className="mt-2 text-sm text-accent-600 dark:text-accent-400 font-medium">
            {selected.length} program{selected.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </motion.div>

      {/* Category sections */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {categoryOrder.map((category) => (
          <CategorySection
            key={category}
            category={category}
            programs={programsByCategory[category]}
            selectedPrograms={selected}
            onToggle={toggleProgram}
            isExpanded={expandedCategories.has(category)}
            onToggleExpand={() => toggleCategory(category)}
          />
        ))}

        {/* Custom Programs Section */}
        <div className="mt-6 bg-surface-100 dark:bg-surface-800/50 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-surface-800 dark:text-white mb-3 flex items-center gap-2">
            <PlusIcon className="w-5 h-5 text-accent-500" />
            Other Programs
          </h3>
          <p className="text-xs text-surface-500 dark:text-surface-400 mb-4">
            Don't see your program? Add it here and we'll consider including it in future versions.
          </p>

          {/* List of custom programs */}
          {data.customPrograms && data.customPrograms.length > 0 && (
            <div className="space-y-2 mb-4">
              {data.customPrograms.map((program) => (
                <div
                  key={program.id}
                  className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-600"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-surface-800 dark:text-white text-sm">
                        {program.name}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 dark:bg-accent-900/50 dark:text-accent-300">
                        {program.abbreviation}
                      </span>
                    </div>
                    {program.description && (
                      <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                        {program.description}
                      </p>
                    )}
                    {program.website && (
                      <p className="text-xs text-accent-600 dark:text-accent-400 mt-0.5">
                        {program.website}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomProgram(program.id)}
                    className="flex-shrink-0 p-1 text-danger-500 hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add custom program form */}
          <div className="space-y-3">
            <Input
              label="Program Name *"
              placeholder="e.g., Overeaters Anonymous, SMART Recovery"
              value={newProgramName}
              onChange={(e) => setNewProgramName(e.target.value)}
              maxLength={50}
            />
            <Input
              label="Abbreviation *"
              placeholder="e.g., OA, SMART"
              value={newProgramAbbreviation}
              onChange={(e) => {
                setNewProgramAbbreviation(e.target.value);
                setAbbreviationError('');
              }}
              maxLength={10}
              hint="Short code to identify your program (2-10 characters)"
              error={abbreviationError}
            />
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                Description (Optional)
              </label>
              <textarea
                value={newProgramDescription}
                onChange={(e) => setNewProgramDescription(e.target.value)}
                placeholder="Brief description of the program"
                rows={2}
                maxLength={200}
                className="w-full px-4 py-2.5 rounded-lg border-2 border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700 text-surface-800 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none text-sm"
              />
              <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                {newProgramDescription.length}/200 characters
              </p>
            </div>
            <Input
              label="Website (Optional)"
              placeholder="https://..."
              value={newProgramWebsite}
              onChange={(e) => setNewProgramWebsite(e.target.value)}
              type="url"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={handleAddCustomProgram}
              disabled={!newProgramName.trim() || !newProgramAbbreviation.trim()}
              className="w-full"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Program
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Submit button */}
      <motion.div
        className="pt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button
          onClick={handleSubmit}
          size="lg"
          className="w-full"
          disabled={selected.length === 0}
        >
          {selected.length === 0 ? 'Select at least one program' : 'Continue'}
        </Button>
      </motion.div>
    </div>
  );
}
